import { captureErrorInApm } from '../../../lib/apm/utils/captureErrorInApm.util';
import { PaginatedEndpointParams } from '../../../services/types';
import { offlineDatabase } from '../database/offlineDatabase/offlineDatabase';
import { OfflineDatabaseOrder } from '../database/offlineDatabase/types/offlineDatabaseOrder.types';
import { OfflineOrder } from '../domain/offlineOrder/OfflineOrder';
import { OfflineStatus } from '../domain/offlineOrder/offlineOrder.types';
import { OfflineOrderTransformers } from '../transformers/OfflineOrder.transformers';
import { OfflineDataEncryption } from '../utils/offlineDataEncryption/OfflineDataEncryption.util';
import { waitOfflineEncryptionKey } from '../utils/waitOfflineEncryptionKey.util';

const waitForEncryptionKeyIfNeeded = async () => {
  const hasOrders = await offlineOrdersRepository.hasOrders();

  if (!hasOrders) {
    return;
  }

  await waitOfflineEncryptionKey();
};

export const offlineOrdersRepository = {
  save: async (order: OfflineOrder) => {
    try {
      const dataToCreateOfflineOrder = OfflineOrderTransformers.toCreateOfflineDatabaseOrder(order);

      const orderCode = await offlineDatabase.orders.add(dataToCreateOfflineOrder);

      const orderWithCode: OfflineDatabaseOrder = {
        ...dataToCreateOfflineOrder,
        code: orderCode,
      };

      return OfflineOrder.fromOfflineDatabase(orderWithCode);
    } catch (e) {
      const error = e as Error;
      captureErrorInApm.offlineError(error);
      throw error;
    }
  },
  delete: async (code: number) => {
    await offlineDatabase.orders.delete(code);
  },
  deleteMany: async (codes: number[]) => {
    await offlineDatabase.orders.bulkDelete(codes);
  },
  update: async (order: OfflineOrder) => {
    try {
      const dataToUpdateOfflineOrder = OfflineOrderTransformers.toOfflineDatabaseOrder(order);

      await offlineDatabase.orders.update(
        order.code,
        dataToUpdateOfflineOrder as Partial<OfflineDatabaseOrder>,
      );

      return order;
    } catch (e) {
      const error = e as Error;
      captureErrorInApm.offlineError(error);
      throw error;
    }
  },
  updateMany: async (orders: OfflineOrder[]) => {
    const dataToUpdateOfflineOrders = orders.map(OfflineOrderTransformers.toOfflineDatabaseOrder);

    await offlineDatabase.orders.bulkPut(dataToUpdateOfflineOrders);

    return orders;
  },
  getById: async (id?: string) => {
    const order = await offlineDatabase.orders.get({ id });
    if (!order) {
      return;
    }
    return OfflineOrder.fromOfflineDatabase(order);
  },
  getByIdOrThrow: async (id?: string) => {
    const order = await offlineOrdersRepository.getById(id);
    if (!order) {
      throw new Error(`offline order [${id}] not found`);
    }
    return order;
  },
  countByIds: async (ids: string[]) => {
    const count = await offlineDatabase.orders
      .where('id')
      .anyOf(...ids)
      .count();
    return count;
  },
  findByIds: async (ids: string[]) => {
    await waitForEncryptionKeyIfNeeded();

    const orders = await offlineDatabase.orders
      .where('id')
      .anyOf(...ids)
      .toArray();
    return orders.map(OfflineOrder.fromOfflineDatabase);
  },
  getAll: async () => {
    await waitForEncryptionKeyIfNeeded();

    const orders = await offlineDatabase.orders.toArray();
    return orders.map(OfflineOrder.fromOfflineDatabase);
  },
  page: async ({ limit, skip, query }: PageParams) => {
    await waitForEncryptionKeyIfNeeded();

    let collection = offlineDatabase.orders.toCollection();

    if (query) {
      const offlineDataEncryption = new OfflineDataEncryption();

      collection = collection.filter((order) => {
        const decryptedCustomerCommercialName = order.customer?.commercialName
          ? offlineDataEncryption.decryptOfflineData({
              input: order.customer?.commercialName,
            })
          : undefined;

        return (
          String(order.code).includes(query) ||
          !!decryptedCustomerCommercialName?.toLowerCase().includes(query.toLowerCase())
        );
      });
    }

    const count = await collection.count();
    const orders = await collection.toArray();

    orders.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const paginatedOrders = orders.slice(skip, skip + limit);

    return {
      count,
      data: paginatedOrders.map(OfflineOrder.fromOfflineDatabase),
    };
  },
  count: async () => {
    const count = await offlineDatabase.orders.count();
    return count;
  },
  hasOrders: async () => {
    const count = await offlineDatabase.orders.count();
    return count > 0;
  },
  findSyncingOrders: async () => {
    await waitForEncryptionKeyIfNeeded();

    const orders = await offlineDatabase.orders
      .filter((order) => order.status === OfflineStatus.SYNCING)
      .toArray();
    return orders.map(OfflineOrder.fromOfflineDatabase);
  },
  findSyncingOrdersByIds: async (ids: string[]) => {
    await waitForEncryptionKeyIfNeeded();

    const orders = await offlineDatabase.orders
      .where('id')
      .anyOf(...ids)
      .and((order) => order.status === OfflineStatus.SYNCING)
      .toArray();
    return orders.map(OfflineOrder.fromOfflineDatabase);
  },
};

export interface PageParams extends PaginatedEndpointParams {
  query?: string;
}
